import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Check, ArrowRight, ChevronRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "@/pages/landing/WaitlistModal";
import { openBooking, BOOKING_URL } from "@/lib/booking";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CEREMONIES = [
  "Griha Pravesha (housewarming)", "Satyanarayana Pooja", "Naamakarana (naming)",
  "Vivaha (wedding)", "Rudrabhisheka", "Navagraha Shanti",
  "Ganapathi & Navagraha Homa", "Varamahalakshmi & festival poojas",
];

const STEPS = [
  { n: "1", t: "Tell us your ceremony", d: "Pick the pooja, your date and your area — it takes a minute." },
  { n: "2", t: "Get matched purohiths", d: "Verified purohiths of your tradition and language send you clear, itemised proposals." },
  { n: "3", t: "Confirm with confidence", d: "Choose your purohith, see the full summary, and book — no hidden charges, samagri arranged either way." },
];

export default function LocationPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [waitlist, setWaitlist] = useState(false);

  useEffect(() => {
    setStatus("loading");
    fetch(`${BASE}/api/public/location-pages/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { setData(d); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  useEffect(() => {
    if (status === "notfound") { const t = setTimeout(() => nav("/locations", { replace: true }), 50); return () => clearTimeout(t); }
  }, [status, nav]);

  const loc = data?.location;
  const area = loc?.area || "";
  const faqs = useMemo(() => (loc ? [
    { q: `Do you serve ${area}, Bengaluru?`, a: `Yes. PurohithConnect matches families in ${area} and nearby localities with KYC-verified purohiths for every ceremony, in your tradition and language.` },
    { q: `Which ceremonies can I book in ${area}?`, a: `Griha Pravesha, Satyanarayana Pooja, Naamakarana, weddings, Rudrabhisheka, Navagraha Shanti, homas and festival poojas — all performed with authentic Vedic vidhi.` },
    { q: `Do purohiths bring the samagri?`, a: `Your choice. The purohith can bring everything, or you can get the full item list in advance and arrange it yourself. Every proposal shows a clear itemised summary with no hidden charges.` },
  ] : []), [loc, area]);

  useSeo({
    title: loc ? `${loc.title} | PurohithConnect` : "PurohithConnect",
    description: loc ? (loc.intro?.slice(0, 300) || `Book a verified purohith in ${area}, Bengaluru with PurohithConnect.`) : "",
    canonicalPath: `/locations/${slug}`,
    jsonLd: useMemo(() => (loc ? [
      {
        "@context": "https://schema.org", "@type": "Service",
        serviceType: "Hindu purohith & pooja booking",
        provider: { "@type": "Organization", name: "PurohithConnect", url: "https://purohithconnect.com" },
        areaServed: { "@type": "Place", name: `${area}, Bengaluru, Karnataka, India` },
        name: loc.title, description: loc.intro,
        url: `https://purohithconnect.com/locations/${slug}`,
      },
      {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Areas We Serve", item: "https://purohithconnect.com/locations" },
          { "@type": "ListItem", position: 3, name: area, item: `https://purohithconnect.com/locations/${slug}` },
        ],
      },
    ] : null), [loc, area, slug, faqs]),
  });

  if (status !== "ok" || !loc) {
    return (
      <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
        <SiteHeader openWaitlist={() => setWaitlist(true)} />
        <main className="mx-auto max-w-7xl px-4 py-24 text-center md:px-8">
          <p className="font-mukta text-[#6B5044]" data-testid="location-loading">
            {status === "loading" ? "Loading…" : "Redirecting to all areas…"}
          </p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={() => setWaitlist(true)} />
      <main>
        {/* breadcrumb */}
        <div className="mx-auto max-w-7xl px-4 pt-6 md:px-8">
          <nav className="flex items-center gap-1.5 font-mukta text-xs text-[#8A7A6E]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#3B221B]">Home</Link><ChevronRight size={13} />
            <Link to="/locations" className="hover:text-[#3B221B]">Areas We Serve</Link><ChevronRight size={13} />
            <span className="text-[#3B221B]">{area}</span>
          </nav>
        </div>

        {/* hero */}
        <section className="mx-auto max-w-7xl px-4 pt-6 md:px-8">
          <p className="flex items-center gap-1.5 font-mukta text-sm font-semibold uppercase tracking-widest text-[#A8722A]">
            <MapPin size={15} /> {area}, Bengaluru
          </p>
          <h1 className="mt-2 max-w-3xl font-eczar text-4xl font-bold leading-tight sm:text-5xl" data-testid="location-title">{loc.title}</h1>
          <p className="mt-1 font-baloo text-lg text-[#A8722A]">{area} — ನಿಮ್ಮ ಸಂಪ್ರದಾಯದ ಪುರೋಹಿತರು</p>
          <p className="mt-4 max-w-3xl font-mukta text-base text-[#6B5044] md:text-lg" data-testid="location-intro">{loc.intro}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button onClick={() => openBooking({ location: "location-page" })} data-testid="location-cta"
              className="rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]">Get early access in {area}</button>
            <Link to="/occasions" className="rounded-full border border-[#A8722A] px-6 py-3 font-mukta font-semibold text-[#A8722A] hover:bg-[#A8722A]/10">See all occasions</Link>
          </div>
        </section>

        {/* ceremonies */}
        <section className="mx-auto max-w-7xl px-4 py-14 md:px-8">
          <h2 className="font-eczar text-3xl font-semibold">Ceremonies we cover in {area}</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {CEREMONIES.map((c) => (
              <div key={c} className="flex items-start gap-2 rounded-[12px] border border-[#A8722A]/20 bg-[#FFFDF8] p-4">
                <Check size={17} className="mt-0.5 shrink-0 text-[#3E6B4F]" strokeWidth={2.5} />
                <span className="font-mukta text-sm text-[#3B221B]">{c}</span>
              </div>
            ))}
          </div>
        </section>

        {/* how it works */}
        <section className="border-y border-[#A8722A]/20 bg-[#F3EADA]">
          <div className="mx-auto max-w-7xl px-4 py-14 md:px-8">
            <h2 className="text-center font-eczar text-3xl font-semibold">How booking a purohith in {area} works</h2>
            <div className="mt-9 grid gap-6 md:grid-cols-3">
              {STEPS.map((s) => (
                <div key={s.n} className="rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm" data-testid={`location-step-${s.n}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#B23A2B] font-eczar text-lg font-bold text-[#FFFDF8]">{s.n}</span>
                  <h3 className="mt-4 font-eczar text-lg font-semibold text-[#3B221B]">{s.t}</h3>
                  <p className="mt-1.5 font-mukta text-sm text-[#6B5044]">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-4xl px-4 py-14 md:px-8">
          <h2 className="font-eczar text-3xl font-semibold">Common questions — {area}</h2>
          <div className="mt-6 space-y-3" data-testid="location-faqs">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-[12px] border border-[#A8722A]/25 bg-[#FFFDF8] p-4" open={i === 0}>
                <summary className="cursor-pointer font-eczar text-lg font-semibold text-[#3B221B]">{f.q}</summary>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* related areas */}
        {data.related?.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 pb-16 md:px-8">
            <h2 className="font-eczar text-2xl font-semibold">Other areas we serve</h2>
            <div className="mt-5 flex flex-wrap gap-3" data-testid="location-related">
              {data.related.map((r) => (
                <Link key={r.slug} to={`/locations/${r.slug}`} data-testid={`location-related-${r.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#A8722A]/30 bg-[#FFFDF8] px-4 py-2 font-mukta text-sm font-medium text-[#6B5044] hover:border-[#B23A2B] hover:text-[#3B221B]">
                  <MapPin size={14} className="text-[#B23A2B]" /> {r.area} <ArrowRight size={13} />
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      <WaitlistModal key={waitlist} open={waitlist} defaultRole="Family" onClose={() => setWaitlist(false)} />
    </div>
  );
}
